// @ts-nocheck

import type { Option, QrCodeSettings } from '@branchjs/neo-components';
import B from 'bluebird';
import debugLib from 'debug';
import * as R from 'ramda';

import dash_api from '../support/dash_api';
import server from '../support/server';

import { ReduxAsyncAction } from '../support/utils';

import { createDismissFilter, getDismissPeriodFromFilter } from '../screens/ScreenJourneys/utils/journeyHelpers';

import type { View } from '../components/micro-frontends/journeys/Creatives/creativesTypes';
import { sortRows } from '../components/micro-frontends/journeys/frontendPaginate';
import { findNextCopyName, findRuleById, getHighestPriority } from '../screens/ScreenJourneys/utils/RuleUtils';
import AppsStore from '../stores/AppsStore_LEGACY';
import type { Filter, RuleType } from '../types/journeyTypes';

const debug = debugLib('JourneyActions');

function getViewsForRule(rule, views) {
  return R.filter((view) => {
    return R.contains(
      view.id,
      R.map((bv) => bv.branch_view_id, rule.branch_views)
    );
  }, views);
}

export const APP_LOADED = 'JOURNEYS_APP_LOADED';
export function fetchApp() {
  return { type: APP_LOADED, app: AppsStore.getApp() };
}

const FetchAppStats = new ReduxAsyncAction('JOURNEYS_FETCH_APP_STATS');
FetchAppStats.registerRequest(() => {
  return dash_api.post('/v1/sdk/get-status').then(R.prop('data'));
});
export const FETCH_APP_STATS = FetchAppStats.constants;
export const fetchAppStats = FetchAppStats.request;

const FetchRules = new ReduxAsyncAction('JOURNEYS_FETCH_RULES');
FetchRules.registerRequest((prioritize) => {
  return server('audience rule list load').then(
    (res) => {
      if (prioritize) {
        const clonedRules = Object.assign([], res);
        return sortRows(clonedRules, 'priority');
      } else {
        return res;
      }
    },
    (err) => {
      throw err;
    }
  );
});
export const FETCH_RULES = FetchRules.constants;
export const fetchRules = FetchRules.request;

export const ALL_VIEWS_DICTIONARY = 'JOURNEYS_ALL_VIEWS_DICTIONARY';
export function allViewsDictionary(branch_views = []) {
  return { type: ALL_VIEWS_DICTIONARY, payload: { branch_views } };
}

const FetchViews = new ReduxAsyncAction('JOURNEYS_FETCH_VIEWS');
FetchViews.registerRequest(() => {
  return server('branch view list load').then((view_list) => {
    FetchViews.dispatch(allViewsDictionary(view_list));
    return view_list;
  });
});
export const FETCH_VIEWS = FetchViews.constants;
export const fetchViews = FetchViews.request;

const SaveRule = new ReduxAsyncAction('JOURNEYS_SAVE_RULE');
SaveRule.registerRequest((id, data, rules, withVersionIncrease: boolean = false, withVersionNotes: boolean = false) => {
  id = id === 'new' ? null : id;

  var priority = data.priority;

  if (!priority && Array.isArray(rules)) {
    // if priority is 0 or undefined, calculate the highest pri
    priority = getHighestPriority(rules) + 1;
  }

  const rule_data = R.assoc('priority', priority, data);

  if (withVersionNotes) {
    rule_data.customer_version_note = rule_data.new_customer_version_note;
  } else {
    rule_data.customer_version_note = undefined;
  }

  return dash_api.post('/v2/callback/audience%20rule%20save', {
    rule_id: id,
    rule_data: {
      ...rule_data,
      create_version: withVersionIncrease,
    },
  });
});
export const SAVE_RULE = SaveRule.constants;
export const saveRule = SaveRule.request;

const SaveView = new ReduxAsyncAction('JOURNEYS_SAVE_VIEW');
SaveView.registerRequest((id, view) => {
  debug(SaveView.constants.response, { id, view });

  const view_data = R.clone(view);
  // Remove any data fields that are null OR that we don't want to save
  const innerData = view_data && view_data.data;

  if (innerData) {
    let parsed = innerData;
    if (typeof innerData === 'string') {
      try {
        parsed = JSON.parse(innerData);
        // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
      } catch (e) {
        parsed = {};
      }
    }
    Object.keys(parsed).forEach((field) => {
      // JOUR-1876: dismissPeriod is now saved in the rule query, not on the view
      if (parsed[field] === null || field === 'dismissPeriod') {
        delete parsed[field];
      }
    });
    view_data.data = JSON.stringify(parsed);
  }

  return server('branch view save', {
    view_id: id,
    view_data: view_data,
  });
});
export const SAVE_VIEW = SaveView.constants;
export const saveView = SaveView.request;

const DeleteRule = new ReduxAsyncAction('JOURNEYS_DELETE_RULE');
DeleteRule.registerRequest((id, title) => {
  return server('audience rule delete', {
    rule_id: id,
    title,
  }).then(() => id);
});
export const DELETE_RULE = DeleteRule.constants;
export const deleteRule = DeleteRule.request;

const DeleteView = new ReduxAsyncAction('JOURNEYS_DELETE_VIEW');
DeleteView.registerRequest((id, title) => {
  return server('branch view delete', {
    view_id: id,
    title,
  }).then(() => id);
});
export const DELETE_VIEW = DeleteView.constants;
export const deleteView = DeleteView.request;

export function updateView(view_id, view, view_index, editing_rule_id) {
  return function (dispatch) {
    return dispatch(saveView(view_id, view)).then((branch_view) => {
      dispatch(branchViewLoaded(branch_view));

      if (editing_rule_id) {
        dispatch(setBranchViewForRule(editing_rule_id, view_index, branch_view.id));
      }
      return branch_view.id;
    });
  };
}

export function deleteRuleAndViews(id, rules, views, title?: string) {
  return function (dispatch) {
    return dispatch(deleteRule(id, title))
      .then(() => {
        const rule = findRuleById(rules, id);
        const viewsToDelete = getViewsForRule(rule, views);

        const promises = viewsToDelete.map((view) => {
          return dispatch(deleteView(view.id, view.title));
        });

        return B.all(promises);
      })
      .then(() => {
        dispatch(fetchRules());
        dispatch(fetchViews());
        return;
      });
  };
}

const sortViewsBy = (key) => {
  return (a, b) => {
    if (!a.hasOwnProperty(key) || !b.hasOwnProperty(key)) {
      return 0;
    } else if (a[key] < b[key]) {
      return -1;
    } else if (a[key] > b[key]) {
      return 1;
    }
    return 0;
  };
};

export function cloneRule(id, rules, views) {
  return function clone(dispatch) {
    const rule = findRuleById(rules, id);
    const sortedRuleBranchViews = R.sort(sortViewsBy('branch_view_id'), rule.branch_views);

    if (!rule) {
      return;
    }

    const viewsToBeCloned = R.sort(
      sortViewsBy('id'),
      R.filter((view) => {
        return R.contains(
          view.id,
          R.map((bv) => bv.branch_view_id, rule.branch_views)
        );
      }, views)
    );

    if (viewsToBeCloned.length === 0) {
      dispatch(
        saveView(
          'new',
          R.merge(rule, {
            status: 'DRAFT',
            title: findNextCopyName(rules, rule.title),
            campaign: findNextCopyName(rules, rule.campaign),
            priority: null,
          }),
          rules
        )
      );
      return;
    }

    // if there are views associated, we want to make sure they
    // all get cloned properly before saving the rule
    const promises = R.map((view) => {
      return dispatch(saveView(null, view));
    }, viewsToBeCloned);

    B.all(promises)
      .then((clonedList) => {
        const cloneIdPairings = sortedRuleBranchViews.map((view, i) => ({
          preCloneId: view.branch_view_id,
          postCloneId: R.path([i, 'id'], clonedList),
        }));
        // reassociate the cloned views with the rule before saving
        const newBranchViews = sortedRuleBranchViews.map((view, i) => {
          const clonedId = R.path([i, 'id'], clonedList);
          return R.assoc('branch_view_id', clonedId, view);
        });

        return { cloneIdPairings, newBranchViews };
      })
      .then(({ cloneIdPairings, newBranchViews }) => {
        // JOUR-1876: dismissPeriod is now saved in the rule query, not on the view
        cloneIdPairings.forEach((idPair) => {
          const preClonePeriod = getDismissPeriodFromFilter(idPair.preCloneId, rule);
          const preCloneFilter = createDismissFilter(preClonePeriod, idPair.preCloneId);
          const postCloneFilter = createDismissFilter(preClonePeriod, idPair.postCloneId);

          // 1. Delete the pre-cloned view from the query
          rule.builder.findAndDeleteFilter(preCloneFilter);
          // 2. Add the cloned view to the query
          rule.builder.findAndUpdateFilter(postCloneFilter);
          rule.query = rule.builder.getQuery();
        });
        // save the new rule into draft mode with incremented name and cloned views
        return dispatch(
          saveRule(
            'new',
            R.merge(rule, {
              status: 'DRAFT',
              title: findNextCopyName(rules, rule.title),
              campaign: findNextCopyName(rules, rule.campaign),
              branch_views: newBranchViews,
              priority: null,
            }),
            rules
          )
        );
      })
      .then(() => {
        dispatch(fetchRules());
        dispatch(fetchViews());
        return;
      })
      .catch((err) => {
        dispatch({ type: 'JOURNEY_CLONE_RULE_ERROR', data: err });
      });
  };
}

export const UPDATE_SCRATCHPAD_RULE = 'JOURNEYS_UPDATE_SCRATCHPAD_RULE';
export function updateScratchpadRule(key, value) {
  return {
    type: UPDATE_SCRATCHPAD_RULE,
    data: { key, value },
  };
}

export const UPDATE_SCRATCHPAD_VIEW = 'JOURNEYS_UPDATE_SCRATCHPAD_VIEW';
export function updateScratchpadView(key, value) {
  return {
    type: UPDATE_SCRATCHPAD_VIEW,
    data: { key, value },
  };
}

export const SELECT_RULE_FOR_EDIT = 'JOURNEYS_SELECT_RULE_FOR_EDIT';
export function selectRuleForEdit(rule_id) {
  return {
    type: SELECT_RULE_FOR_EDIT,
    data: { rule_id },
  };
}

export const CLEAR_RULE_FOR_EDIT = 'JOURNEYS_CLEAR_RULE_FOR_EDIT';
export function clearRuleForEdit() {
  return { type: CLEAR_RULE_FOR_EDIT };
}

export const SELECT_VIEW_FOR_EDIT = 'JOURNEYS_SELECT_VIEW_FOR_EDIT';
export function selectViewForEdit(view_id) {
  return {
    type: SELECT_VIEW_FOR_EDIT,
    data: { view_id },
  };
}

export const ADD_VIEW_FOR_RULE = 'JOURNEYS_ADD_VIEW_FOR_RULE';
export function addViewForRule(rule_id) {
  return {
    type: ADD_VIEW_FOR_RULE,
    data: { rule_id },
  };
}

export const REMOVE_VIEW_FOR_RULE = 'JOURNEYS_REMOVE_VIEW_FOR_RULE';
export function removeViewForRule(rule_id, view_index) {
  return {
    type: REMOVE_VIEW_FOR_RULE,
    data: { rule_id, view_index },
  };
}

export const SET_VIEW_FREQUENCY = 'JOURNEYS_SET_VIEW_FREQUENCY';
export function setViewFrequency(rule_id, view_index, freq) {
  return {
    type: SET_VIEW_FREQUENCY,
    data: { rule_id, view_index, freq },
  };
}

export const PRIORITY_SORT = 'JOURNEYS_PRIORITY_SORT';
export function prioritySort() {
  return {
    type: PRIORITY_SORT,
  };
}

const SaveReorderedRules = new ReduxAsyncAction('JOURNEYS_SAVE_REORDERED_RULES');
SaveReorderedRules.registerRequest((rules) => {
  const promises = [];

  R.addIndex(R.forEach)((rule, i) => {
    if (rule.status === 'ARCHIVED') {
      return;
    }
    if (i !== rule.priority - 1) {
      rule.priority = i + 1;

      promises.push(
        dash_api.post('/v2/callback/audience%20rule%20save', {
          rule_id: rule.id,
          rule_data: rule,
        })
      );
    }
  }, rules);

  return B.all(promises);
});
SaveReorderedRules.showProgressBar();
export const SAVE_REORDERED_RULES = SaveReorderedRules.constants;
export const saveReorderedRules = SaveReorderedRules.request;

const SaveReorderPriorityFromPopover = new ReduxAsyncAction('JOURNEYS_SAVE_REORDERED_PRIORITY_POPOVER');
SaveReorderPriorityFromPopover.registerRequest((ruleId: string, priority: number, prioritize: boolean) => {
  return server('audience rule priority save', {
    ruleId,
    priority,
    // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
  }).then((res) => {
    if (prioritize) {
      return SaveReorderPriorityFromPopover.dispatch(fetchRules(prioritize));
    } else {
      return SaveReorderPriorityFromPopover.dispatch(fetchRules());
    }
  });
});
export const saveReorderPriorityFromPopover = SaveReorderPriorityFromPopover.request;

export const REORDER_TABLE = 'JOURNEYS_REORDER_TABLE';
export function reorderTable(fromId, toId) {
  return {
    type: REORDER_TABLE,
    data: { fromId, toId },
  };
}

export const GET_VIEWS_AVAILABLE_FOR_PLATFORM = 'JOURNEYS_GET_VIEWS_AVAILABLE_FOR_PLATFORM';
export function getViewsAvailableForPlatform(templateTypeOptions, platform) {
  return {
    type: GET_VIEWS_AVAILABLE_FOR_PLATFORM,
    payload: { templateTypeOptions, platform },
  };
}

export const GET_LANGUAGE_TABLE = 'JOURNEYS_GET_LANGUAGE_TABLE';
export function getLanguageTable() {
  return function getInfo(dispatch) {
    return server('string table load').then((data) => {
      const tables = R.values(data);

      dispatch({
        type: GET_LANGUAGE_TABLE,
        data: tables,
      });
    });
  };
}

export const SAVE_LANGUAGE_TABLE = 'JOURNEYS_SAVE_LANGUAGE_TABLE';
export function saveLanguageTable(table) {
  return function saveInfo(dispatch) {
    return server('string table save', {
      table: table,
    }).then((data) => {
      dispatch({
        type: SAVE_LANGUAGE_TABLE,
        data: data,
      });
    });
  };
}

/**
 * Store the journey Id so that a warning modal is not
 * shown for trying to save the journey until page is refreshed
 * @param  {string} journeyId
 * @return {Object} redux action
 */
export const JOURNEY_DISMISS_SAVE_WARNING = 'JOURNEY_DISMISS_SAVE_WARNING';
export function dismissSaveWarningForJourney(journeyId) {
  return {
    type: JOURNEY_DISMISS_SAVE_WARNING,
    data: journeyId,
  };
}

export const UPDATE_SCRATCHPAD_RULE_BUILDER_ADD_BY_PATH = 'JOURNEYS_UPDATE_SCRATCHPAD_RULE_BUILDER_ADD_BY_PATH';
export function updateScratchpadRuleBuilderAddByPath(path, value) {
  return {
    type: UPDATE_SCRATCHPAD_RULE_BUILDER_ADD_BY_PATH,
    data: { path, value },
  };
}

export const UPDATE_SCRATCHPAD_RULE_BUILDER_REMOVE_BY_PATH = 'JOURNEYS_UPDATE_SCRATCHPAD_RULE_BUILDER_REMOVE_BY_PATH';
export function updateScratchpadRuleBuilderRemoveByPath(path, value) {
  return {
    type: UPDATE_SCRATCHPAD_RULE_BUILDER_REMOVE_BY_PATH,
    data: { path, value },
  };
}

export const UPDATE_SCRATCHPAD_RULE_TYPE = 'JOURNEYS_UPDATE_SCRATCHPAD_RULE_TYPE';
export function updateScratchpadRuleType(type: RuleType) {
  return {
    type: UPDATE_SCRATCHPAD_RULE_TYPE,
    data: { type },
  };
}

const SearchGeoRegions = new ReduxAsyncAction('JOURNEYS_SEARCH_GEO_REGIONS');
SearchGeoRegions.registerRequest((query) => {
  return dash_api.get(`/v1/audience-rule/regions/search?q=${encodeURIComponent(query)}`).then(R.prop('data'));
});
export const SEARCH_GEO_REGIONS = SearchGeoRegions.constants;
export const searchGeoRegions = SearchGeoRegions.request;

const LoadRuleGeoRegions = new ReduxAsyncAction('JOURNEYS_LOAD_RULE_GEO_REGIONS');
LoadRuleGeoRegions.registerRequest((partialRegions) => {
  return dash_api.post('/v1/audience-rule/regions/expand', { regions: partialRegions }).then(R.prop('data'));
});
export const LOAD_RULE_GEO_REGIONS = LoadRuleGeoRegions.constants;
export const loadRuleGeoRegions = LoadRuleGeoRegions.request;

export const UPDATE_CREATIVES_PAGINATION_CURRENT_PAGE = 'UPDATE_CREATIVES_PAGINATION_CURRENT_PAGE';
export function updateCreativesPaginationCurrentPage(page: number) {
  return {
    type: UPDATE_CREATIVES_PAGINATION_CURRENT_PAGE,
    data: page,
  };
}

export const UPDATE_CREATIVES_PAGINATION_PAGE_DATA = 'UPDATE_CREATIVES_PAGINATION_PAGE_DATA';
export function updateCreativesPaginationPageData(pageData: View[]) {
  return {
    type: UPDATE_CREATIVES_PAGINATION_PAGE_DATA,
    data: pageData,
  };
}

export const UPDATE_CREATIVES_PAGINATION_FILTERED_VIEWS = 'UPDATE_CREATIVES_PAGINATION_FILTERED_VIEWS';
export function updateCreativesPaginationFilteredViews(filteredViews: View[]) {
  return {
    type: UPDATE_CREATIVES_PAGINATION_FILTERED_VIEWS,
    data: filteredViews,
  };
}

export const RESET_CREATIVES_PAGINATION = 'RESET_CREATIVES_PAGINATION';
export function resetCreativesPagination() {
  return { type: RESET_CREATIVES_PAGINATION };
}

export const UPDATE_CREATIVES_FILTERS_AUTHOR = 'UPDATE_CREATIVES_FILTERS_AUTHOR';
export function updateCreativesFiltersAuthor(author: string) {
  return {
    type: UPDATE_CREATIVES_FILTERS_AUTHOR,
    data: author,
  };
}

export const UPDATE_CREATIVES_FILTERS_DEVICE_TYPE = 'UPDATE_CREATIVES_FILTERS_DEVICE_TYPE';
export function updateCreativesFiltersDeviceType(deviceType: Option) {
  return {
    type: UPDATE_CREATIVES_FILTERS_DEVICE_TYPE,
    data: deviceType,
  };
}

export const UPDATE_CREATIVES_FILTERS_CREATIVE_TYPE = 'UPDATE_CREATIVES_FILTERS_CREATIVE_TYPE';
export function updateCreativesFiltersCreativeType(creativeType: Option) {
  return {
    type: UPDATE_CREATIVES_FILTERS_CREATIVE_TYPE,
    data: creativeType,
  };
}

export const UPDATE_CREATIVES_FILTERS_JOURNEY_OPTION = 'UPDATE_CREATIVES_FILTERS_JOURNEY_OPTION';
export function updateCreativesFiltersJourneyOption(journeyOption: Option) {
  return {
    type: UPDATE_CREATIVES_FILTERS_JOURNEY_OPTION,
    data: journeyOption,
  };
}

export const NULLIFY_CREATIVES_FILTERS = 'NULLIFY_CREATIVES_FILTERS';
export function nullifyCreativesFilters() {
  return {
    type: NULLIFY_CREATIVES_FILTERS,
  };
}

export const UPDATE_SCRATCHPAD_VIEW_QR_CODE_SETTINGS = 'UPDATE_SCRATCHPAD_VIEW_QR_CODE_SETTINGS';
export function updateScratchpadViewQrCodeSettings(settings: QrCodeSettings | null) {
  return {
    type: UPDATE_SCRATCHPAD_VIEW_QR_CODE_SETTINGS,
    data: settings,
  };
}

export const UPDATE_SCRATCHPAD_VIEW_QR_CODE_PREVIEW_URL = 'UPDATE_SCRATCHPAD_VIEW_QR_CODE_PREVIEW_URL';
export function updateScratchpadViewQrCodePreviewUrl(qrCodePreviewUrl: string | null) {
  return {
    type: UPDATE_SCRATCHPAD_VIEW_QR_CODE_PREVIEW_URL,
    data: qrCodePreviewUrl,
  };
}

export const JOURNEYS_LOADING_STATUS_LOADING = 'JOURNEYS_LOADING_STATUS_LOADING';
export function updateJourneyLoadingStatusLoading() {
  return {
    type: JOURNEYS_LOADING_STATUS_LOADING,
  };
}

export const JOURNEYS_LOADING_STATUS_LOADED = 'JOURNEYS_LOADING_STATUS_LOADED';
export function updateJourneyLoadingStatusLoaded() {
  return {
    type: JOURNEYS_LOADING_STATUS_LOADED,
  };
}

export const RESTORE_SCRATCHPAD_VIEW_QR_CODE_SETTINGS = 'RESTORE_SCRATCHPAD_VIEW_QR_CODE_SETTINGS';
export function restoreScratchpadViewQrCodeSettings() {
  return {
    type: RESTORE_SCRATCHPAD_VIEW_QR_CODE_SETTINGS,
  };
}

export const SYNC_SCRATCHPAD_VIEW_QR_CODE_SETTINGS = 'SYNC_SCRATCHPAD_VIEW_QR_CODE_SETTINGS';
export function syncScratchpadViewQrCodeSettings() {
  return {
    type: SYNC_SCRATCHPAD_VIEW_QR_CODE_SETTINGS,
  };
}

export const SET_BRANCH_VIEW_FOR_RULE = 'SET_BRANCH_VIEW_FOR_RULE';
export function setBranchViewForRule(rule_id, view_index, view_id) {
  return {
    type: SET_BRANCH_VIEW_FOR_RULE,
    rule_id,
    view_index,
    view_id,
  };
}

export const BRANCH_VIEW_LOADED = 'BRANCH_VIEW_LOADED';
export function branchViewLoaded(branch_view) {
  return {
    type: BRANCH_VIEW_LOADED,
    branch_view,
  };
}

export const UPDATE_SCRATCHPAD_RULE_FILTERS = 'UPDATE_SCRATCHPAD_RULE_FILTERS';
export function updateScratchpadRuleFilters(filters: Filter[]) {
  return { type: UPDATE_SCRATCHPAD_RULE_FILTERS, filters };
}

export const FIND_AND_UPDATE_SCRATCHPAD_RULE_FILTER = 'FIND_AND_UPDATE_SCRATCHPAD_RULE_FILTER';
export function findAndUpdateScratchpadRuleFilter(filter: Filter) {
  return { type: FIND_AND_UPDATE_SCRATCHPAD_RULE_FILTER, filter };
}

export const FIND_AND_DELETE_SCRATCHPAD_RULE_FILTER = 'FIND_AND_DELETE_SCRATCHPAD_RULE_FILTER';
export function findAndDeleteScratchpadRuleFilter(filter: Filter) {
  return { type: FIND_AND_DELETE_SCRATCHPAD_RULE_FILTER, filter };
}

export const OVERRIDE_ORIGINAL_SCRATCHPAD_RULE = 'OVERRIDE_ORIGINAL_SCRATCHPAD_RULE';
export function overrideOriginalScratchpadRule() {
  return { type: OVERRIDE_ORIGINAL_SCRATCHPAD_RULE };
}

export const REVERT_SCRATCHPAD_RULE_STATE = 'REVERT_SCRATCHPAD_RULE_STATE';
export function revertScratchpadRuleState() {
  return { type: REVERT_SCRATCHPAD_RULE_STATE };
}

export const UPDATE_SCRATCHPAD_RULE_VERSION_NOTE = 'UPDATE_SCRATCHPAD_RULE_VERSION_NOTE';
export function updateScratchpadRuleVersionNote(note: string) {
  return { type: UPDATE_SCRATCHPAD_RULE_VERSION_NOTE, data: note };
}
